
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PartnerWithDetails, PartnerPricing } from '@/types/partner';
import { useCentralizedAuth } from '@/context/CentralizedAuthProvider';
import { useBookings } from '@/hooks/useBookings';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Clock } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

// Available time slots
const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', '13:00', 
  '14:00', '15:00', '16:00', '17:00', '18:00'
];

const BookingDialogPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useCentralizedAuth();
  const { createBooking, applyCouponToBooking, loading } = useBookings();
  
  // Get passed state
  const state = location.state as {
    partner: PartnerWithDetails;
    pricing: PartnerPricing;
    associationType: string;
    storageKey?: string;
  } | null;
  
  if (!state) {
    // Redirect back if no state was passed
    navigate('/career-counselors');
    return null;
  }
  
  const { partner, pricing, associationType, storageKey } = state;
  
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [couponCode, setCouponCode] = useState<string>('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(pricing.session_charge);
  const [bookingCreated, setBookingCreated] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  
  // Get the association ID for this type
  const association = partner.associations.find(a => a.name === associationType);
  
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }
    
    setIsApplyingCoupon(true);
    try {
      const { discount, finalAmount } = await applyCouponToBooking(pricing.session_charge, couponCode);
      setDiscount(discount);
      setFinalAmount(finalAmount);
      
      if (discount > 0) {
        toast.success("Coupon applied successfully!");
      } else {
        toast.error("Invalid or expired coupon code");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      toast.error("Error applying coupon. Please try again.");
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleCreateBooking = async () => {
    if (!isAuthenticated) {
      toast.error("Please log in to book a session");
      navigate("/login");
      return;
    }
    
    if (!selectedDate || !selectedTime || !association) {
      toast.error("Please select a date and time for your session");
      return;
    }
    
    // Combine date and time
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const sessionDateTime = new Date(selectedDate);
    sessionDateTime.setHours(hours, minutes, 0, 0);
    
    // Check if the selected date and time is in the past
    if (sessionDateTime < new Date()) {
      toast.error("Please select a future date and time");
      return;
    }
    
    // Create booking with coupon code if available
    const result = await createBooking(
      partner.id,
      association.id,
      pricing.id,
      sessionDateTime,
      pricing.min_session_time,
      pricing.session_charge,
      discount > 0 ? couponCode : undefined,
      Boolean(storageKey) // Flag for data from session storage
    );
    
    if (result) {
      setBookingCreated(true);
      setBookingId(result.id);
      toast.success("Booking created! Proceed to payment to confirm.");
    }
  };
  
  const handleProceedToPayment = () => {
    navigate(`/book-session?partnerId=${partner.id}&associationId=${association?.id}&bookingId=${bookingId}${storageKey ? `&storageKey=${storageKey}` : ''}`);
  };
  
  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-6 flex items-center text-muted-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <div className="w-10 h-10 bg-gray-200 rounded-full mr-3 flex items-center justify-center overflow-hidden">
                  {partner.profile_image ? (
                    <img src={partner.profile_image} alt={partner.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-semibold">{partner.name.charAt(0)}</span>
                  )}
                </div>
                Book a Session with {partner.name}
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              {!bookingCreated ? (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="date">Select Date</Label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date() || date > new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
                      className="rounded-md border pointer-events-auto"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="time">Select Time</Label>
                    <Select
                      value={selectedTime}
                      onValueChange={setSelectedTime}
                    >
                      <SelectTrigger id="time" className="w-full">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((time) => (
                          <SelectItem key={time} value={time}>
                            <div className="flex items-center">
                              <Clock className="mr-2 h-4 w-4" />
                              {time}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="couponCode">Coupon Code (Optional)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="couponCode"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        placeholder="Enter coupon code"
                        disabled={isApplyingCoupon || loading}
                      />
                      <Button 
                        onClick={handleApplyCoupon} 
                        variant="outline" 
                        disabled={isApplyingCoupon || loading || !couponCode.trim()}
                      >
                        {isApplyingCoupon ? "Applying..." : "Apply"}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-md border">
                    <h3 className="font-medium mb-2">Session Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Session Duration:</span>
                        <span>{pricing.min_session_time} minutes</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Original Price:</span>
                        <span>{pricing.session_charge} {pricing.currency}</span>
                      </div>
                      
                      {discount > 0 && (
                        <div className="flex justify-between items-center text-green-600">
                          <span className="text-sm">Discount:</span>
                          <span>-{discount} {pricing.currency}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center font-semibold pt-2 border-t mt-2">
                        <span>Final Price:</span>
                        <span className="text-lg">{finalAmount} {pricing.currency}</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleCreateBooking} 
                    disabled={!selectedDate || !selectedTime || loading}
                    className="w-full"
                  >
                    {loading ? "Creating Booking..." : "Book Now"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-green-50 text-green-800 p-4 rounded-md border border-green-200">
                    <h3 className="font-medium text-green-800 mb-2">Booking Created Successfully!</h3>
                    <p>Your session is scheduled. Please complete the payment to confirm your booking.</p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-md border">
                    <h3 className="font-medium mb-2">Session Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Counselor:</span>
                        <span>{partner.name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Date & Time:</span>
                        <span>
                          {selectedDate && format(selectedDate, 'PPP')} at {selectedTime}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Duration:</span>
                        <span>{pricing.min_session_time} minutes</span>
                      </div>
                      <div className="flex justify-between items-center font-semibold pt-2 border-t mt-2">
                        <span>Total Amount:</span>
                        <span className="text-lg">{finalAmount} {pricing.currency}</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleProceedToPayment}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    Proceed to Payment <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BookingDialogPage;
